//Ghost user hook

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  initializeUserPresence,
  updateUserPosition,
  updateUserPresence,
  subscribeToUserPresence,
  removeUserPresence
} from '../services/userPresenceService';
import { UserPresence } from '../types';
import { activityManager } from '../services/activityManager';

export const useUserPresence = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentForest } = useSelector((state: RootState) => state.forest);
  
  const [ghostUsers, setGhostUsers] = useState<UserPresence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [presenceActive, setPresenceActive] = useState(false);
  
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const positionUpdateQueueRef = useRef<NodeJS.Timeout | null>(null);
  const lastSentPositionRef = useRef<{ x: number; y: number } | null>(null);
  const presenceInitializedRef = useRef<boolean>(false);
  
  //Init users on forest load
  useEffect(() => {
    const initializePresence = async () => {
      if (!currentForest?.id || !user?.uid || !activityManager.isActive()) {
        return;
      }

      if (presenceInitializedRef.current) {
        console.log('Presence already initialized for this forest');
        return;
      }

      try {
        console.log('Initializing user presence in forest:', currentForest.id);
        
        await initializeUserPresence(
          currentForest.id,
          user.uid,
          user.username,
          user.initials,
          user.lastPosition || { x: 32, y: 32 }, //Default center position
          user.preferredColor || 'blue'
        );
        
        presenceInitializedRef.current = true;
        console.log('User presence initialized successfully');
      } catch (error) {
        console.error('Failed to initialize user presence:', error);
      }
    };

    //Reset init on forest change
    presenceInitializedRef.current = false;
    initializePresence();

    return () => {
      presenceInitializedRef.current = false;
    };
  }, [currentForest?.id, user?.uid]);

  //Subscribe to user positions
  useEffect(() => {
    if (!currentForest?.id || !user?.uid) {
      console.log('No forest or user, skipping presence subscription');
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let activityCheckInterval: NodeJS.Timeout | null = null;

    const startPresenceSubscription = () => {
      if (unsubscribe) return;
      
      console.log('Starting presence subscription for forest:', currentForest.id);
      setIsLoading(true);
      setError(null);
      setPresenceActive(true);

      unsubscribe = subscribeToUserPresence(
        currentForest.id,
        user.uid,
        (users) => {
          if (activityManager.isActive()) {
            console.log(`Received ${users.length} ghost users`);
            setGhostUsers(users);
          } else {
            console.log('Ignoring ghost users update - user inactive');
          }
          setIsLoading(false);
        },
        (err) => {
          console.error('Presence subscription error:', err);
          setError(err.message);
          setIsLoading(false);
        }
      );
    };

    const stopPresenceSubscription = () => {
      if (!unsubscribe) return;
      
      console.log('Stopping presence subscription');
      unsubscribe();
      unsubscribe = null;
      setPresenceActive(false);
      setGhostUsers([]); //Clear ghosts when inactive
    };

    //Init
    if (activityManager.isActive()) {
      startPresenceSubscription();
    } else {
      console.log('User inactive on mount, no presence subscription started');
    }

    //Activity monitoring
    activityCheckInterval = setInterval(() => {
      const isUserActive = activityManager.isActive();
      const hasActiveSubscription = unsubscribe !== null;

      if (isUserActive && !hasActiveSubscription) {
        console.log('User active, starting presence subscription');
        startPresenceSubscription();
      } else if (!isUserActive && hasActiveSubscription) {
        console.log('User inactive, stopping presence subscription');
        stopPresenceSubscription();
      }
    }, 30000);

    unsubscribeRef.current = () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
        setPresenceActive(false);
      }
    };

    return () => {
      console.log('Presence subscription cleanup');
      if (unsubscribe) {
        unsubscribe();
        setPresenceActive(false);
      }
      if (activityCheckInterval) {
        clearInterval(activityCheckInterval);
      }
    };
  }, [currentForest?.id, user?.uid]);

  //Update user position
  const updatePosition = useCallback(async (x: number, y: number) => {
    if (!activityManager.isActive()) {
      console.log('User inactive, skipping position update');
      return;
    }

    if (!currentForest?.id || !user?.uid) {
      console.log('Cannot update position - missing data:', {
        forestId: currentForest?.id,
        hasUser: !!user
      });
      return;
    }

    //Check if pos changed
    const lastPos = lastSentPositionRef.current;
    if (lastPos && lastPos.x === x && lastPos.y === y) {
      console.log('Position unchanged, skipping update');
      return; 
    }

    console.log('Updating position (optimized):', {
      forestId: currentForest.id,
      userId: user.uid,
      position: { x, y },
      previousPosition: lastPos
    });

    try {
      //write new pos
      await updateUserPosition(currentForest.id, user.uid, { x, y });
      
      lastSentPositionRef.current = { x, y };
      console.log('Position update successful (optimized)');
    } catch (error) {
      console.error('Failed to update position:', error);
    }
  }, [currentForest?.id, user?.uid]);

  //Handle color/status changes
  const updateNonPositionData = useCallback(async (updates: {
    username?: string;
    initials?: string;
    preferredColor?: string;
    isActive?: boolean;
  }) => {
    if (!currentForest?.id || !user?.uid) {
      console.log('Cannot update user presence - missing data');
      return;
    }

    try {
      console.log('Updating non-position user data:', updates);
      await updateUserPresence(currentForest.id, user.uid, updates);
      console.log('Non-position data update successful');
    } catch (error) {
      console.error('Failed to update user presence:', error);
    }
  }, [currentForest?.id, user?.uid]);

  //Pos update
  const handlePositionChange = useCallback((x: number, y: number) => {
    if (!activityManager.isActive()) {
      console.log('User inactive, ignoring position change');
      return;
    }

    console.log('handlePositionChange called (optimized):', { x, y });
    
    //Clear pending updates
    if (positionUpdateQueueRef.current) {
      clearTimeout(positionUpdateQueueRef.current);
    }

    //Update
    positionUpdateQueueRef.current = setTimeout(() => {
      if (activityManager.isActive()) {
        console.log('Executing delayed position update (optimized)');
        updatePosition(x, y);
      } else {
        console.log('User became inactive, canceling position update');
      }
    }, 200);
  }, [updatePosition]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentForest?.id && user?.uid) {
        navigator.sendBeacon('/api/user-inactive', JSON.stringify({
          forestId: currentForest.id,
          userId: user.uid
        }));
        
        removeUserPresence(currentForest.id, user.uid).catch(console.error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentForest?.id, user?.uid]);

  useEffect(() => {
    return () => {
      if (positionUpdateQueueRef.current) {
        clearTimeout(positionUpdateQueueRef.current);
      }
      
      if (currentForest?.id && user?.uid) {
        removeUserPresence(currentForest.id, user.uid).catch(console.error);
      }
    };
  }, [currentForest?.id, user?.uid]);

  //Helpers
  const getGhostUsersAt = useCallback((x: number, y: number) => {
    return ghostUsers.filter(user => user.position.x === x && user.position.y === y);
  }, [ghostUsers]);

  const hasGhostUserAt = useCallback((x: number, y: number) => {
    return ghostUsers.some(user => user.position.x === x && user.position.y === y);
  }, [ghostUsers]);

  const getGhostUsersInViewport = useCallback((
    startX: number, 
    startY: number, 
    endX: number, 
    endY: number
  ) => {
    return ghostUsers.filter(user => 
      user.position.x >= startX && 
      user.position.x <= endX &&
      user.position.y >= startY && 
      user.position.y <= endY
    );
  }, [ghostUsers]);

  return {
    //State
    ghostUsers,
    isLoading,
    error,
    
    //Actions
    handlePositionChange,
    updateNonPositionData,
    
    //Helpers
    getGhostUsersAt,
    hasGhostUserAt,
    getGhostUsersInViewport,
    
    //Stats
    totalGhostUsers: ghostUsers.length,
    isPresenceActive: !!currentForest?.id && !!user?.uid
  };
};